import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight, LogIn } from 'lucide-react';

interface EmailExistsModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
  redirectDelay?: number;
}

export function EmailExistsModal({
  isOpen,
  onClose,
  email,
  redirectDelay = 5,
}: EmailExistsModalProps) {
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Auto-redirect after specified delay
      timerRef.current = setTimeout(() => {
        handleSignIn();
      }, redirectDelay * 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen, redirectDelay]);

  const handleSignIn = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onClose();
    navigate('/crm');
  };

  const handleTryDifferentEmail = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleTryDifferentEmail()}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md overflow-hidden">
        <AnimatePresence mode="wait">
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <DialogHeader className="space-y-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 260,
                    damping: 20,
                    delay: 0.1,
                  }}
                  className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center"
                >
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <DialogTitle className="text-xl font-semibold text-center text-white">
                    Welcome Back!
                  </DialogTitle>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <DialogDescription className="text-center text-slate-400">
                    Welcome back! Signing you into your account...
                    {email && (
                      <span className="block mt-1 text-slate-300 font-medium">
                        {email}
                      </span>
                    )}
                  </DialogDescription>
                </motion.div>
              </DialogHeader>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-6 space-y-3"
              >
                <Button
                  onClick={handleSignIn}
                  className="w-full bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white py-5 font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Continue to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <Button
                  variant="outline"
                  onClick={handleTryDifferentEmail}
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white py-5 transition-all duration-200"
                >
                  Try Different Email
                </Button>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-center text-xs text-slate-500 mt-4"
                >
                  You will be automatically redirected after{' '}
                  {redirectDelay} seconds
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

export default EmailExistsModal;
